import { Component, OnInit, Inject, signal, computed } from '@angular/core';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import {
  FormGroup,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  SavedViewsService,
  GRID_NAME_TO_TRANSLATION,
} from '@/app/modules/saved-views/saved-views.service';
import { MatButtonModule } from '@angular/material/button';
import { LogService } from '@/app/core';

export interface SaveAsDialogData {
  onSave: (reportName: string, setAsDefault: boolean) => Promise<any>;
}

@Component({
  selector: 'rdo-saved-views-save-as-dialog',
  styleUrls: ['./saved-views-dialogs.component.scss'],
  template: `
    <div [id]="gridName" class="save-views-save-as-dialog saved-views-dialog">
      <form [formGroup]="form" class="dialog-form">
        <h2 mat-dialog-title class="header">{{ currentTitle() }}</h2>
        <div mat-dialog-content class="content">
          <div>
            <label class="text-label">
              {{ 'main.saved_views.dialogs.save_as.report_name' | translate }}:
              <input
                type="text"
                formControlName="reportName"
                placeholder="{{
                  'main.saved_views.dialogs.save_as.placeholder' | translate
                }}:"
                [disabled]="isSaving()"
                class="text-input"
              />
            </label>
          </div>
          @if (errorMessage()) {
            <div class="error-message">
              <i class="fa fa-exclamation-circle"></i>{{ errorMessage() }}
            </div>
          }
        </div>
        <div mat-dialog-actions class="actions">
          <div class="actions-left">
            <label class="checkbox-label">
              <input
                type="checkbox"
                formControlName="setAsDefault"
                class="checkbox"
                [disabled]="isSaving()"
              />
              {{
                'main.saved_views.dialogs.save_as.set_as_default' | translate
              }}:
            </label>
          </div>
          <div class="actions-right">
            @if (isSaving()) {
              <div class="saving-indicator">
                <div class="rdo-loader">
                  <img
                    src="/assets/images/loader-sm.gif"
                    width="20"
                    height="20"
                  />
                </div>
              </div>
            }
            <button
              mat-button
              class="btn"
              (click)="onOkClick()"
              [disabled]="!form.valid || isSaving()"
            >
              OK
            </button>
            <button
              mat-button
              mat-dialog-close
              class="btn"
              [disabled]="isSaving()"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  `,
  imports: [
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class SavedViewsSaveAsDialogComponent implements OnInit {
  public gridName: string = '';
  public form: FormGroup;
  public isSaving = signal(false);
  public errorMessage = signal('');
  public currentTitle = computed(() => {
    const title = this.getTitle();
    return title;
  });

  private logger = this.logService.getModuleLogger(
    'SavedViews',
    'SavedViewsSaveAsDialogComponent',
    false
  );

  constructor(
    private dialogRef: MatDialogRef<SavedViewsSaveAsDialogComponent>,
    private translateService: TranslateService,
    private savedViewsService: SavedViewsService,
    private logService: LogService,
    @Inject(MAT_DIALOG_DATA) public data: SaveAsDialogData
  ) {}

  ngOnInit(): void {
    this.logger.info('ngOnInit', this.savedViewsService.currentGridName());
    const str =
      GRID_NAME_TO_TRANSLATION[this.savedViewsService.currentGridName()] || '';
    this.gridName = this.translateService.instant(str);
    this.form = new FormGroup({
      reportName: new FormControl('', Validators.required),
      setAsDefault: new FormControl(false),
    });
  }

  async onOkClick(): Promise<void> {
    if (this.form.valid && !this.isSaving()) {
      const formData = this.form.value;
      formData.reportName = this.savedViewsService.getSerializedViewName(
        formData.reportName
      );
      this.isSaving.set(true);
      this.errorMessage.set('');

      try {
        if (this.data && this.data.onSave) {
          await this.data.onSave(formData.reportName, formData.setAsDefault);
        }
        // Close dialog on success
        this.dialogRef.close({ success: true, ...formData });
      } catch (error) {
        this.isSaving.set(false);
        this.errorMessage.set(
          error.message || 'Failed to save. Please try again.'
        );
        this.logger.error('Save error:', error);
      }
    }
  }

  public getTitle(): string {
    const result = this.translateService.instant(
      'main.saved_views.dialogs.save_as.title',
      { report_name: this.gridName }
    );
    this.logger.info('getTitle  result:', result);
    return result;
  }
}
